import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  createPlaylistApi,
  deletePlaylistApi,
  fetchPlaylistDetailsApi,
  fetchPlaylists,
  fetchPlaylistsByShowId,
  fetchPlaylistTracksApi,
  removeTrackFromPlaylistApi,
  resetProcessedPlaylistApi,
  updatePlaylistActionApi,
  updatePlaylistApi,
  updatePlaylistJingleApi,
  updatePlaylistRecommendationTagsApi,
  updatePlaylistStatusApi,
  submitPlaylistApi,
  updatePlaylistSubtagsApi,
  updatePlaylistTagsApi,
  updatePlaylistTrackOrderApi,
} from "@apis/playlistApis";
import { QUERY_KEYS } from "./queryKeys";
import {
  CreatePlaylistBody,
  IPlaylistRecord,
  PlaylistActionType,
  PlaylistStatus,
} from "@src/types/playlist";
import { AxiosResponse } from "axios";
import { ApiResponse, SortingParams } from "@src/types/httpApi";
import { IPlaylistFilter } from "@src/redux/slices/playlistSlice";

export const useInfinitePlaylists = (
  filtersApplied: IPlaylistFilter & { sorting: SortingParams } & {
    curatorId: string;
    role: string;
  },
  options?: any
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_PLAYLISTS, filtersApplied],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchPlaylists({
        ...filtersApplied,
        sorting: filtersApplied.sorting,
        page: pageParam,
        pageSize: 50,
      }),
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<IPlaylistRecord[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    gcTime: 5000,
    staleTime: (1000 * 60) / 2,
    enabled: !!(
      filtersApplied.curatorId ||
      filtersApplied.role === "admin" ||
      filtersApplied.role === "manager"
    ),
    ...options,
  });
};
export const useCreatePlaylistMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    CreatePlaylistBody,
    unknown
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  CreatePlaylistBody,
  unknown
> => {
  return useMutation<AxiosResponse<any>, Error, CreatePlaylistBody>({
    mutationFn: (data: CreatePlaylistBody) => createPlaylistApi(data),
    ...options,
  });
};

export const useUpdatePlaylistMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    Partial<CreatePlaylistBody & { playlistId: string }>,
    unknown
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { playlistId: string; data: CreatePlaylistBody },
  unknown
> => {
  return useMutation<
    AxiosResponse<any>,
    Error,
    { playlistId: string; data: CreatePlaylistBody }
  >({
    mutationFn: ({
      playlistId,
      data,
    }: {
      playlistId: string;
      data: CreatePlaylistBody;
    }) => updatePlaylistApi(data, playlistId),
    ...options,
  });
};

type UpdatePlaylistTagsVariables = {
  playlistId: string;
  playlistTagsIds: string[];
  /** Left out while `FEATURES.PLAYLIST_SUBTAGS_ENABLED` is off [ROVR-3982]. */
  playlistSubtagsIds?: string[];
};

export const useUpdatePlaylistTagsMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    UpdatePlaylistTagsVariables,
    unknown
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  UpdatePlaylistTagsVariables,
  unknown
> => {
  return useMutation<AxiosResponse<any>, Error, UpdatePlaylistTagsVariables>({
    mutationFn: async ({ playlistId, playlistTagsIds, playlistSubtagsIds }) => {
      const response = await updatePlaylistTagsApi(playlistId, playlistTagsIds);
      if (!playlistSubtagsIds) return response;
      return updatePlaylistSubtagsApi(playlistId, playlistSubtagsIds);
    },
    ...options,
  });
};

export const useUpdatePlaylistRecommendationTagsMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    { playlistId: string; recommendationTagIds: string[] },
    unknown
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { playlistId: string; recommendationTagIds: string[] },
  unknown
> => {
  return useMutation<
    AxiosResponse<any>,
    Error,
    { playlistId: string; recommendationTagIds: string[] }
  >({
    mutationFn: ({
      playlistId,
      recommendationTagIds,
    }: {
      playlistId: string;
      recommendationTagIds: string[];
    }) => updatePlaylistRecommendationTagsApi(playlistId, recommendationTagIds),
    ...options,
  });
};

export const useDeletePlaylistMutation = (
  options?: UseMutationOptions<AxiosResponse<any>, Error, string, unknown>
): UseMutationResult<AxiosResponse<any>, Error, string, unknown> => {
  return useMutation<AxiosResponse<any>, Error, string>({
    mutationFn: (playlistId: string) => deletePlaylistApi(playlistId),
    ...options,
  });
};

export const usePlaylistTracksQuery = (
  playlistId: string,
  filters: { searchText: string },
  options?: UseQueryOptions<AxiosResponse<any>, Error>
): UseQueryResult<AxiosResponse<any>, Error> => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_PLAYLIST_TRACKS, playlistId, filters],
    queryFn: () => fetchPlaylistTracksApi(playlistId, filters),
    ...options,
  });
};

export const usePlaylistDetailsQuery = (
  playlistId: string,
  options?: UseQueryOptions<AxiosResponse<any>, Error>
): UseQueryResult<AxiosResponse<any>, Error> => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_PLAYLIST_DETAILS, playlistId],
    queryFn: () => fetchPlaylistDetailsApi(playlistId),
    ...options,
  });
};
export const usePlaylistUpdateAction = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    {
      playlistId: string;
      action: PlaylistActionType;
    }
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { playlistId: string; action: PlaylistActionType }
> => {
  return useMutation<
    AxiosResponse<any>,
    Error,
    { playlistId: string; action: PlaylistActionType }
  >({
    mutationKey: [QUERY_KEYS.UPDATE_PLAYLIST_ACTION],
    mutationFn: ({ playlistId, action }) =>
      updatePlaylistActionApi(playlistId, action),
    ...options,
  });
};
export const usePlaylistUpdateStatus = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    {
      playlistId: string;
      status: PlaylistStatus;
    }
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  {
    playlistId: string;
    status: PlaylistStatus;
  }
> => {
  return useMutation<
    AxiosResponse<any>,
    Error,
    {
      playlistId: string;
      status: PlaylistStatus;
    }
  >({
    mutationKey: [QUERY_KEYS.UPDATE_PLAYLIST_STATUS],
    mutationFn: ({ playlistId, status }) =>
      updatePlaylistStatusApi(playlistId, status),
    ...options,
  });
};

export const useSubmitPlaylistMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    {
      playlistId: string;
      playlistTagsIds: string[];
      playlistSubtagsIds?: string[];
    }
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  {
    playlistId: string;
    playlistTagsIds: string[];
    playlistSubtagsIds?: string[];
  }
> => {
  return useMutation<
    AxiosResponse<any>,
    Error,
    {
      playlistId: string;
      playlistTagsIds: string[];
      playlistSubtagsIds?: string[];
    }
  >({
    mutationKey: [QUERY_KEYS.SUBMIT_PLAYLIST],
    mutationFn: ({ playlistId, playlistTagsIds, playlistSubtagsIds }) =>
      submitPlaylistApi(playlistId, playlistTagsIds, playlistSubtagsIds),
    ...options,
  });
};

export const usePlaylistTracksOrderAction = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    {
      playlistId: string;
      tracksIds: string[];
    }
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { playlistId: string; tracksIds: string[] }
> => {
  return useMutation<
    AxiosResponse<any>,
    Error,
    { playlistId: string; tracksIds: string[] }
  >({
    mutationKey: [QUERY_KEYS.UPDATE_PLAYLIST_TRACK_ORDER_ACTION],
    mutationFn: ({ playlistId, tracksIds }) =>
      updatePlaylistTrackOrderApi(playlistId, tracksIds),
    ...options,
  });
};

export const useRemoveTrackFromPlaylistAction = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    {
      playlistId: string;
      trackId: string;
    }
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { playlistId: string; trackId: string }
> => {
  return useMutation<
    AxiosResponse<any>,
    Error,
    { playlistId: string; trackId: string }
  >({
    mutationKey: [QUERY_KEYS.REMOVE_TRACK_FROM_PLAYLIST_ACTION],
    mutationFn: ({ playlistId, trackId }) =>
      removeTrackFromPlaylistApi(playlistId, trackId),
    ...options,
  });
};

export const usePlaylistsByShow = ({
  showId,
  searchText,
}: {
  showId: string;
  searchText: string;
}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_PLAYLISTS_BY_SHOW, showId, searchText],
    queryFn: () => fetchPlaylistsByShowId(showId, searchText),
  });
};

export const useResetProcessedPlaylist = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    {
      playlistId: string;
    }
  >
) => {
  return useMutation({
    mutationFn: ({ playlistId }: { playlistId: string }) =>
      resetProcessedPlaylistApi(playlistId),
    ...options,
  });
};

export const useUpdatePlaylistJingleMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    { playlistId: string; jingleDocumentId: string | null }
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { playlistId: string; jingleDocumentId: string | null }
> => {
  return useMutation<
    AxiosResponse<any>,
    Error,
    { playlistId: string; jingleDocumentId: string | null }
  >({
    mutationKey: [QUERY_KEYS.UPDATE_PLAYLIST_JINGLE],
    mutationFn: ({ playlistId, jingleDocumentId }) =>
      updatePlaylistJingleApi(playlistId, jingleDocumentId),
    ...options,
  });
};
